import * as React from "react";

import type { StokedBarGroup } from "../../../../../../parts/ad-copy/stocked-bar-graph";
import { AdCopyStockedBarGraph } from "../../../../../../parts/ad-copy/stocked-bar-graph";
import { AdCopyUserTable } from "./user-table";
import { Constant } from "../../../../../../../../../server/analytics/v3/output";
import {
	CommonCurrentProductData,
	SimulationPredictionData,
	SimulationReferenceSourceData,
} from "../../../../../../../../../server/types/request/simulation";
import { AdCopyCP2 } from "../../../../../../../../../server/models/ad-copy/cp2";
import { makeSimulator } from "../../../../../../../../../shared/simulation";
import { changeCorrectionValues } from "../../../../../../../../../server/lib/common";
import { SellPotentialValue } from "../../../../../report/simulation/result/result-table";
import { AdCopyResultTable } from "./result-table";
import { AdCopyCPCorrectionValueTable } from "../../../simulation/result/cp-correction-value";
import { AdCopyGoalTable } from "../../../simulation/result/goal-table";

type Props = {
	constValue: Constant;
	currentProduct?: CommonCurrentProductData<AdCopyCP2>;
	data: SimulationPredictionData;
	goalSalesVolume: number;
	isExistingBrand: boolean;
	referenceSourceData: SimulationReferenceSourceData;
	goalRecognitionRate: number;
};

export const AdCopySimulationResult = React.memo((props: Props) => {
	const {
		constValue,
		currentProduct,
		data,
		goalSalesVolume,
		isExistingBrand,
		referenceSourceData,
		goalRecognitionRate,
	} = props;

	const { correctionValue, initialValue } = data;

	const sellPotentials = React.useMemo<SellPotentialValue[]>(() => {
		const { calc } = makeSimulator(referenceSourceData, currentProduct?.initialValue, constValue, goalRecognitionRate);

		const isCpChange = currentProduct?.correctionValue?.correctionFactor != null;
		const cpResult = calc(initialValue.purchaseIntent, {}, currentProduct?.correctionValue);
		const result = calc(initialValue.purchaseIntent, correctionValue, currentProduct?.correctionValue);
		const changed = changeCorrectionValues(correctionValue, referenceSourceData, goalRecognitionRate);

		const sellPotentials: SellPotentialValue[] = [
			{
				label: "初期値（万個）",
				sellPotential: {
					total: initialValue.total,
					trial: initialValue.trial,
					repeat: initialValue.repeat,
					localOther: initialValue.localOther,
					inbound: initialValue.ibby,
				},
			},
			{
				label: "①実績値 修正後\n（万個）",
				sellPotential: isCpChange
					? {
							total: cpResult.total,
							trial: cpResult.trial,
							repeat: cpResult.repeat,
							localOther: cpResult.localOther,
							inbound: cpResult.ibby,
					  }
					: {
							inbound: undefined,
							localOther: undefined,
							repeat: undefined,
							total: undefined,
							trial: undefined,
					  },
			},
			{
				label: "②テスト品 変数補整後\n（万個）",
				sellPotential: changed
					? {
							total: result.total,
							trial: result.trial,
							repeat: result.repeat,
							localOther: result.localOther,
							inbound: result.ibby,
					  }
					: {
							inbound: undefined,
							localOther: undefined,
							repeat: undefined,
							total: undefined,
							trial: undefined,
					  },
			},
		];

		return sellPotentials;
	}, [
		constValue,
		correctionValue,
		currentProduct?.correctionValue,
		currentProduct?.initialValue,
		goalRecognitionRate,
		initialValue.ibby,
		initialValue.localOther,
		initialValue.purchaseIntent,
		initialValue.repeat,
		initialValue.total,
		initialValue.trial,
		referenceSourceData,
	]);

	const salesData = React.useMemo<StokedBarGroup[]>(() => {
		return sellPotentials.map(({ label, sellPotential }) => ({
			bars: [
				{ color: "#f56302", label: "トライアル", value: sellPotential.trial },
				{ color: "#ffc000", label: "リピート", value: sellPotential.repeat },
				{ color: "#83db4d", label: "ローカルその他", value: sellPotential.localOther },
				{ color: "#6492e3", label: "インバウンド", value: sellPotential.inbound },
			],
			label,
			total: sellPotential.total,
		}));
	}, [sellPotentials]);

	const graphBase = React.useMemo(() => {
		const tmp = Math.max(...salesData.map((d) => d.bars.reduce((a, b) => a + (b.value || 0), 0)), goalSalesVolume);

		if (tmp < 10) {
			const max = Math.floor(tmp + 1);

			return {
				max,
				step: Math.floor((max / 10) * 100) / 100,
			};
		}

		const max = Math.ceil(Math.round(tmp / 10) + 1) * 10;

		let step = Math.ceil(max / 10);

		if (5 < step && step < 10) {
			step = 10;
		} else if (step % 10 !== 0) {
			step = Math.ceil(step / 10) * 10;
		}

		return {
			max,
			step,
		};
	}, [goalSalesVolume, salesData]);

	return (
		<table className="wrap-table">
			<tbody>
				<tr>
					<td className="table-area">
						{/* 基人口、購入意向ワンナンバースコア、定数テーブル */}
						<AdCopyUserTable
							constValue={constValue}
							isExistingBrand={isExistingBrand}
							user={referenceSourceData.user}
							segmentPurchaseIntent={data.initialValue.purchaseIntent}
						/>

						{/* 判定基準テーブル */}
						<AdCopyGoalTable goalSalesVolume={goalSalesVolume} />

						{/* 実績補整係数テーブル */}
						{(currentProduct?.correctionValue?.correctionFactor != null ||
							currentProduct?.initialValue?.correctionFactor != null) && (
							<AdCopyCPCorrectionValueTable
								cpCorrectionValue={
									currentProduct.correctionValue.correctionFactor ?? currentProduct.initialValue.correctionFactor
								}
							/>
						)}

						{/* 推定値テーブル */}
						<AdCopyResultTable
							hasCurrentProduct={currentProduct?.correctionValue?.correctionFactor != null}
							sellPotentials={sellPotentials}
							goalSalesVolume={goalSalesVolume}
						/>
					</td>

					{/* グラフ */}
					<td>
						<AdCopyStockedBarGraph
							barGroups={salesData}
							lineValue={goalSalesVolume}
							max={graphBase.max}
							step={graphBase.step}
						/>
					</td>
				</tr>
			</tbody>
		</table>
	);
});
